import React from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import PathPickMembershipBillingCycleToggle from "@/features/pathpick/features/membership/components/PathPickMembershipBillingCycleToggle";
import PathPickMembershipPlanCard from "@/features/pathpick/features/membership/components/plan-compare-section/PathPickMembershipPlanCard";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  getPlanRelation,
  matchesPlanCode,
  MEMBERSHIP_COLORS,
  MEMBERSHIP_PLANS,
} from "@/features/pathpick/features/membership/constants";
import {
  MembershipBillingCycle,
  MembershipPlan,
} from "@/features/pathpick/features/membership/types";

interface Props {
  billingCycle: MembershipBillingCycle;
  onChangeBillingCycle: (billingCycle: MembershipBillingCycle) => void;
  currentPlan: MembershipPlan | undefined;
  // 신규 가입(첫 달 프로모션 대상)이면 설명 문구와 가격 표기가 정가 대비 형태로 바뀐다.
  isFirstSubscription: boolean;
  onChangeRequest?: (plan: MembershipPlan) => void;
  // 시작 가이드처럼 비교 관점이 다른 화면에서 설명 문구만 갈아끼운다.
  descriptionOverride?: string;
  // 플랜별 CTA 노출 여부 — 끄면 한도/가격을 읽기만 하는 화면이 된다(시작 가이드).
  hasCta?: boolean;
  // 무료체험 가능 바이어에게 신규(none) CTA를 [무료체험|플랜 시작하기] 두 버튼으로 가른다(시안).
  onStartTrial?: () => void;
  // 상담하기(enterprise) 동작 override — 주지 않으면 기본대로 채널톡을 연다(패스픽 랜딩).
  onConsult?: (plan: MembershipPlan) => void;
  // 입금 대기 중인 신청의 플랜 코드("BASIC") — 그 카드만 CTA가 "신청 중"이 된다.
  // 넘기지 않으면 모든 카드가 평소대로 신청을 받는다(랜딩·시작 가이드).
  pendingPlanCode?: string | null;
}

// 신규 가입은 "정가 대비 할인 안내", 기존 회원은 "현재 플랜 대비 비교"로 설명이 갈린다.
const DESCRIPTION_KEYS = {
  promo: {
    monthly: "membership:compare.description_promo_monthly",
    yearly: "membership:compare.description_promo_yearly",
  },
  regular: {
    monthly: "membership:compare.description_monthly",
    yearly: "membership:compare.description_yearly",
  },
} as const;

// 플랜 비교 섹션 — 결제 주기 토글 + 플랜 카드 그리드
//
// 멤버십 화면 외에 시작 가이드와 패스픽 랜딩도 이 컴포넌트를 그대로 가져다 쓴다.
// 두 화면은 "가격·한도를 보여 주기만 하는 화면"이라 공통 위치로 올리지 않고
// 원래 자리(membership feature)에 두기로 한 예외다 — 올리면 상수·타입까지 함께 끌려온다.
function PathPickMembershipPlanCompareSection({
  billingCycle,
  onChangeBillingCycle,
  currentPlan,
  isFirstSubscription,
  descriptionOverride,
  onChangeRequest,
  hasCta = true,
  onStartTrial,
  onConsult,
  pendingPlanCode,
}: Props) {
  const { t } = useI18nHelper();

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <Flex
        justifyContent={"space-between"}
        // 토글은 줄지 않고 제목·설명도 minWidth 0이 없어 이 행이 카드 내부 가용
        // 폭(343px 화면에서 313px)을 넘긴다. 모바일에서는 위아래로 접는다.
        flexDirection={{ base: "column", md: "row" }}
        alignItems={{ base: "stretch", md: "center" }}
        gap={"14px"}
        padding={"14px"}
        border={`1px solid ${MEMBERSHIP_COLORS.cardBorder}`}
        borderRadius={"14px"}
        background={MEMBERSHIP_COLORS.cardBg}
      >
        <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
          <Text
            textStyle={"body-md-bold"}
            fontSize={"16px"}
            color={MEMBERSHIP_COLORS.textStrong}
          >
            {t("membership:compare.title")}
          </Text>
          <Text
            textStyle={"body-sm-regular"}
            color={MEMBERSHIP_COLORS.textMuted}
          >
            {descriptionOverride ??
              t(
                DESCRIPTION_KEYS[isFirstSubscription ? "promo" : "regular"][
                  billingCycle
                ]
              )}
          </Text>
        </Flex>
        <PathPickMembershipBillingCycleToggle
          value={billingCycle}
          isFirstSubscription={isFirstSubscription}
          onChange={onChangeBillingCycle}
        />
      </Flex>
      <Grid
        // auto-fit minmax(280px)는 320px 기기(가용 288px)에서 간신히 한 칸이라
        // 여유가 없다. 모바일은 아예 1열로 고정한다.
        templateColumns={{
          base: "1fr",
          md: "repeat(auto-fit, minmax(280px, 1fr))",
        }}
        gap={"16px"}
        alignItems={"stretch"}
      >
        {MEMBERSHIP_PLANS.map((plan) => (
          <PathPickMembershipPlanCard
            key={plan.id}
            plan={plan}
            billingCycle={billingCycle}
            relation={getPlanRelation(plan, currentPlan)}
            isFirstSubscription={isFirstSubscription}
            onChangeRequest={onChangeRequest}
            hasCta={hasCta}
            onStartTrial={onStartTrial}
            onConsult={onConsult}
            isApplicationPending={matchesPlanCode(plan, pendingPlanCode)}
          />
        ))}
      </Grid>
    </Flex>
  );
}

export default PathPickMembershipPlanCompareSection;
